<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { PlaceholdersService, WordDocumentTemplatesService } from '@/types/generated/services.gen'
  import {
    type PatchedTWordDocumentTemplateRequest as PatchedTemplateRequest,
    type SuperUserWordDocumentTemplate,
    ResourceTypeEnum,
    WordTemplateStatus
  } from '@/types/generated/types.gen'
  import { ROUTES } from '@/router/routes'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import FileField from '@/components/admin/form/FileField.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import {
    CopyIcon,
    DownloadIcon,
    EditIcon,
    EllipsisVerticalIcon,
    FileTextIcon,
    Loader2,
    Columns2Icon
  } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
  import * as yup from 'yup'
  import { useToast } from '@/components/ui/toast'
  import {
    loadFromLocalStorage,
    removeFromLocalStorage,
    saveToLocalStorage
  } from '@/lib/localStorage'
  import OfficeEditor from '@/components/app/editor/OfficeEditor.vue'
  import MicrosoftOfficeViewer from '@/components/app/editor/MicrosoftOfficeViewer.vue'

  defineOptions({
    inheritAttrs: false
  })

  const router = useRouter()
  const route = useRoute()
  const { t } = useTranslation({
    en: {
      alreadyApproved: 'Already Approved',
      name: 'Name',
      description: 'Description',
      systemPrompt: 'System Prompt',
      document: 'Document',
      generatePlaceholders: 'Generate Placeholders',
      validateTemplate: 'Validate Template',
      download: 'Download',
      downloadOriginal: 'Download Original',
      downloadGenerated: 'Download Generated',
      noDocxTemplate: 'No DOCX template available',
      noAiDocxTemplate: 'No AI-generated template available',
      wordTemplates: 'Word Templates',
      placeholdersGenerated: 'Template with placeholders has been generated',
      templateDetails: 'Template Details',
      acceptTemplate: 'Accept Template',
      acceptTemplateConfirmation:
        'Are you sure you want to accept this template? This will mark it as reviewed and make it available for use.',
      cancel: 'Cancel',
      accept: 'Accept',
      underReview: 'Under Review',
      save: 'Approve Template',
      saveChanges: 'Approve Template',
      noDescription: 'No description provided',
      uploadDocument: 'Upload Document',
      replaceDocument: 'Replace Document',
      preview: 'Preview',
      preparingPreview: 'Preparing preview...',
      showPlaceholders: 'Show Empty Placeholders',
      fillWithData: 'Fill with Sample Data',
      useOriginalTemplate: 'Use Original Template',
      useAiTemplate: 'Use AI Template',
      originalTemplate: 'Original Template',
      currentTemplate: 'Current Template',
      editTemplate: 'Edit Template',
      livingDoc: 'Current docx template',
      aiDoc: 'AI generated docx template',
      copyAiToCurrent: 'Copy to current',
      copyToCurrent: 'Copy to current',
      aiGeneratedTemplate: 'AI Generated Template',
      copyAiConfirmTitle: 'Copy AI Template?',
      copyAiConfirmMessage:
        'Do you want to make the AI-generated template the current template? This will overwrite the current working version.',
      copyOriginalConfirmTitle: 'Copy Original Template?',
      copyOriginalConfirmMessage:
        'Do you want to make the original template the current template? This will overwrite the current working version.',
      confirm: 'Confirm',
      copied: 'Copied',
      templateCopied: 'Template copied successfully',
      placeholderGenerationFailed: 'Placeholder Generation Failed',
      connectionError: 'Connection Error',
      connectionLost: 'Connection Lost',
      monitoringError: 'Monitoring Error',
      websocketConnectionFailed: 'WebSocket connection failed. Please try again.',
      websocketConnectionClosed:
        'WebSocket connection closed unexpectedly ({code}). Please try again.',
      failedToStartMonitoring:
        'Failed to start monitoring placeholder generation. Please try again.',
      openEditor: 'Open Editor',
      openEditorDescription: 'Open the document in the full editor to make changes',
      sideBySide: 'Side by Side',
      compareView: 'Compare View',
      originalVersion: 'Original Version',
      currentVersion: 'Current Version (Editable)',
      closeComparison: 'Close'
    },
    de: {
      alreadyApproved: 'Bereits genehmigt',
      name: 'Name',
      description: 'Beschreibung',
      systemPrompt: 'System-Prompt',
      document: 'Dokument',
      generatePlaceholders: 'Platzhalter generieren',
      validateTemplate: 'Vorlage validieren',
      download: 'Herunterladen',
      downloadOriginal: 'Original herunterladen',
      downloadGenerated: 'Generierte Version herunterladen',
      noDocxTemplate: 'Keine DOCX-Vorlage verfügbar',
      noAiDocxTemplate: 'Keine KI-generierte Vorlage verfügbar',
      wordTemplates: 'Word Vorlagen',
      placeholdersGenerated: 'Vorlage mit Platzhaltern wurde generiert',
      templateDetails: 'Vorlagendetails',
      acceptTemplate: 'Vorlage akzeptieren',
      acceptTemplateConfirmation:
        'Sind Sie sicher, dass Sie diese Vorlage akzeptieren möchten? Dies markiert sie als überprüft und macht sie zur Verwendung verfügbar.',
      cancel: 'Abbrechen',
      accept: 'Akzeptieren',
      underReview: 'Wird überprüft',
      save: 'Vorlage genehmigen',
      saveChanges: 'Vorlage genehmigen',
      noDescription: 'Keine Beschreibung vorhanden',
      uploadDocument: 'Dokument hochladen',
      replaceDocument: 'Dokument ersetzen',
      preview: 'Vorschau',
      preparingPreview: 'Vorschau wird vorbereitet...',
      showPlaceholders: 'Leere Platzhalter anzeigen',
      fillWithData: 'Mit Beispieldaten füllen',
      useOriginalTemplate: 'Original-Vorlage verwenden',
      useAiTemplate: 'KI-Vorlage verwenden',
      originalTemplate: 'Original-Vorlage',
      currentTemplate: 'Aktuelle Vorlage',
      editTemplate: 'Vorlage bearbeiten',
      livingDoc: 'Aktuelle docx-Vorlage',
      aiDoc: 'KI-generierte docx-Vorlage',
      copyAiToCurrent: 'Als aktuelle kopieren',
      copyToCurrent: 'Als aktuelle kopieren',
      aiGeneratedTemplate: 'KI-generierte Vorlage',
      copyAiConfirmTitle: 'KI-Vorlage kopieren?',
      copyAiConfirmMessage:
        'Möchten Sie die KI-generierte Vorlage zur aktuellen Vorlage machen? Dies überschreibt die aktuelle Arbeitsversion.',
      copyOriginalConfirmTitle: 'Original-Vorlage kopieren?',
      copyOriginalConfirmMessage:
        'Möchten Sie die Original-Vorlage zur aktuellen Vorlage machen? Dies überschreibt die aktuelle Arbeitsversion.',
      confirm: 'Bestätigen',
      copied: 'Kopiert',
      templateCopied: 'Vorlage wurde erfolgreich kopiert',
      placeholderGenerationFailed: 'Platzhalter-Generierung fehlgeschlagen',
      connectionError: 'Verbindungsfehler',
      connectionLost: 'Verbindung verloren',
      monitoringError: 'Überwachungsfehler',
      websocketConnectionFailed:
        'WebSocket-Verbindung fehlgeschlagen. Bitte versuchen Sie es erneut.',
      websocketConnectionClosed:
        'WebSocket-Verbindung unerwartet geschlossen ({code}). Bitte versuchen Sie es erneut.',
      failedToStartMonitoring:
        'Überwachung der Platzhalter-Generierung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
      openEditor: 'Editor öffnen',
      openEditorDescription:
        'Öffnen Sie das Dokument im vollständigen Editor, um Änderungen vorzunehmen',
      sideBySide: 'Nebeneinander',
      compareView: 'Vergleichsansicht',
      originalVersion: 'Originalversion',
      currentVersion: 'Aktuelle Version (bearbeitbar)',
      closeComparison: 'Schließen'
    }
  })

  const { toast } = useToast()
  const template = ref<SuperUserWordDocumentTemplate | null>(null)
  const placeholders = ref<any[]>([])
  const isGenerating = ref(false)
  const showLoadingDialog = ref(false)
  const isSaving = ref(false)
  const showAcceptModal = ref(false)
  const showCopyAiModal = ref(false)
  const showCopyOriginalModal = ref(false)
  const showSideBySideModal = ref(false)
  const uploadedFile = ref<File | null>(null)
  const fileFieldKey = ref(0)
  // Stable reference to original document URL for side-by-side comparison
  // This prevents the viewer from reloading when template is refreshed
  const originalDocumentUrlForComparison = ref<string | null>(null)

  // Progress tracking for placeholder generation
  const generationProgress = ref(0)
  const generationStatus = ref('')
  const currentTaskId = ref<string | null>(null)
  const currentWebSocket = ref<WebSocket | null>(null)
  const isMonitoring = ref(false)
  const taskErrorHandled = ref(false)
  const getCleanUrl = (url: string | undefined | null) => {
    if (!url) return ''
    return url.split('/').pop()?.split('?')[0] || ''
  }

  const docxTemplateCleanUrl = computed(() => {
    return getCleanUrl(template.value?.docx_template)
  })

  // Key for OfficeEditor to force reload when document changes
  const editorKey = computed(() => {
    // Use office_file_updated_at or updated_at timestamp to force reload when document changes
    const doc = template.value?.document
    if (!doc) return 'no-document'

    // Use office_file_updated_at if available, otherwise fall back to updated_at
    const timestamp = doc.office_file_updated_at || doc.updated_at
    return timestamp ? `${doc.uuid}-${timestamp}` : doc.uuid || 'no-document'
  })

  // Reload key for forcing editor reload
  const editorReloadKey = ref(0)

  const isPreparing = ref<boolean>(false)
  const highlightTexts = computed(() => {
    return placeholderValues.value.map((item) => `{{${item.placeholder}}}`)
  })

  const openAcceptModal = () => {
    showAcceptModal.value = true
  }

  const handleSave = async () => {
    try {
      if (!template.value) return
      isSaving.value = true
      showAcceptModal.value = false

      if (uploadedFile.value) {
        const fileFormData = new FormData()
        fileFormData.append('docx_template', uploadedFile.value)
        fileFormData.append('status', WordTemplateStatus.APPROVED)

        const updatedTemplate =
          (await WordDocumentTemplatesService.wordDocumentTemplatesPartialUpdate({
            uuid: template.value.uuid || '',
            formData: fileFormData as unknown as PatchedTemplateRequest
          })) as SuperUserWordDocumentTemplate

        template.value = updatedTemplate
      } else {
        const dataToSave: PatchedTemplateRequest = {
          status: WordTemplateStatus.APPROVED
        }

        const updatedTemplate =
          (await WordDocumentTemplatesService.wordDocumentTemplatesPartialUpdate({
            uuid: template.value.uuid || '',
            formData: dataToSave
          })) as SuperUserWordDocumentTemplate

        template.value = updatedTemplate
      }

      saveSuccessToast()

      // Reset the file input
      uploadedFile.value = null
    } catch (error) {
      console.error('Error approving template:', error)
      genericErrorToast()
    } finally {
      isSaving.value = false
    }
  }

  // Tab selection between using original template and AI-generated template
  const activeTab = ref(route.query.use_ai_template === 'true' ? 'ai' : 'living')
  const useAiTemplate = computed(() => activeTab.value === 'ai')

  const placeholderValues = computed(() => {
    if (template.value?.replaceable_variables) {
      try {
        // Parse the replaceable_variables if it's a string
        const parsed =
          typeof template.value.replaceable_variables === 'string'
            ? JSON.parse(template.value.replaceable_variables)
            : template.value.replaceable_variables

        if (Array.isArray(parsed)) {
          const placeholdersEntry = parsed.find(
            (entry: any[]) => Array.isArray(entry) && entry[0] === 'placeholders'
          )

          if (placeholdersEntry && Array.isArray(placeholdersEntry[1])) {
            const placeholders = placeholdersEntry[1].map((placeholderItem: any[]) => {
              const placeholderVar = placeholderItem.find(
                (item: any[]) => Array.isArray(item) && item[0] === 'placeholder_variable'
              )

              if (placeholderVar && placeholderVar[1]) {
                const placeholderName = placeholderVar[1]
                return {
                  placeholder: placeholderName,
                  value: `{{${placeholderName}}}`
                }
              }
              return null
            })

            return placeholders.filter(
              (item): item is { placeholder: string; value: string } => item !== null
            )
          }
        }
      } catch (error) {
        console.error('Error processing replaceable_variables:', error)
      }
    }

    return [{ placeholder: 'placeholder', value: 'placeholder' }]
  })

  const getPlaceholderValues = () => {
    return placeholderValues.value
  }

  // Task persistence functions
  interface TaskData {
    taskId: string
    templateUuid: string
    timestamp: number
  }

  const saveTaskToStorage = (taskId: string, templateUuid: string) => {
    const taskData: TaskData = {
      taskId,
      templateUuid,
      timestamp: Date.now()
    }
    saveToLocalStorage(`placeholder_generation_task_${templateUuid}`, taskData)
  }

  const loadTaskFromStorage = (templateUuid: string): TaskData | null => {
    return loadFromLocalStorage<TaskData>(`placeholder_generation_task_${templateUuid}`)
  }

  const clearTaskFromStorage = (templateUuid: string) => {
    removeFromLocalStorage(`placeholder_generation_task_${templateUuid}`)
  }

  const checkForExistingTask = async () => {
    if (!template.value?.uuid) return

    // Prevent duplicate calls
    if (currentTaskId.value) {
      return
    }

    // First check localStorage for task started from this browser
    const taskData = loadTaskFromStorage(template.value.uuid)
    // Also check ai_extraction_metadata for tasks started automatically (e.g., after upload)
    const metadataTaskId = (template.value as any).ai_extraction_metadata
      ?.placeholder_generation_task_id
    const taskIdToCheck = taskData?.taskId || metadataTaskId

    if (taskIdToCheck) {
      // Check if task is still running
      try {
        const data = await WordDocumentTemplatesService.wordDocumentTemplatesTaskStatusRetrieve({
          taskId: taskIdToCheck
        })

        if (data.state === 'PENDING' || data.state === 'PROGRESS') {
          // Task is still running, resume monitoring
          currentTaskId.value = taskIdToCheck
          isGenerating.value = true
          showLoadingDialog.value = true
          generationProgress.value = (data as any).progress || 0
          generationStatus.value = (data as any).status || 'Resuming task...'
          await monitorPlaceholderGeneration(taskIdToCheck)
        } else {
          // Task is completed or failed, clear from storage
          if (taskData?.taskId) {
            clearTaskFromStorage(template.value.uuid)
          }
        }
      } catch (error) {
        console.error('Error checking existing task:', error)
        if (taskData?.taskId) {
          clearTaskFromStorage(template.value.uuid)
        }
      }
    }
  }

  const handleFileUpload = async () => {
    try {
      if (!template.value || !uploadedFile.value) return
      isPreparing.value = true

      const formData: any = {
        name: template.value.name,
        description: template.value.description,
        system_prompt: template.value.system_prompt,
        law_field: template.value.law_field,
        law_topic: template.value.law_topic,
        docx_template: uploadedFile.value
      }

      await WordDocumentTemplatesService.wordDocumentTemplatesPartialUpdate({
        uuid: template.value.uuid || '',
        formData: formData
      })

      const updatedTemplate = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: template.value.uuid || ''
      })) as SuperUserWordDocumentTemplate

      template.value = updatedTemplate
      uploadedFile.value = null
      fileFieldKey.value++

      // Wait a bit for backend to finish processing the document update
      await new Promise((resolve) => setTimeout(resolve, 500))

      // Force editor to reload with the new document
      nextTick(() => {
        editorReloadKey.value++
      })

      isPreparing.value = false
    } catch (error) {
      console.error('Error handling file upload:', error)
      genericErrorToast()
      uploadedFile.value = null
      fileFieldKey.value++
      isPreparing.value = false
    }
  }

  const downloadDOCX = async (useAiVersion = false) => {
    try {
      // Determine which template URL to use
      const templateUrl = useAiVersion
        ? template.value?.ai_docx_template
        : template.value?.docx_template

      if (!templateUrl) {
        genericErrorToast()
        console.error(`No ${useAiVersion ? 'AI-generated' : 'original'} template URL available`)
        return
      }

      const response = await fetch(templateUrl)

      if (!response.ok) {
        console.error(`Error downloading file: ${response.status} ${response.statusText}`)
        genericErrorToast()
        return
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${template.value?.name}${useAiVersion ? '_with_placeholders' : ''}.docx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error in downloadDOCX:', error)
      genericErrorToast()
    }
  }

  const generatePlaceholders = async () => {
    try {
      isGenerating.value = true
      showLoadingDialog.value = true
      generationProgress.value = 0
      generationStatus.value = 'Starting placeholder generation...'

      if (!template.value?.docx_template) {
        genericErrorToast()
        console.error('No docx_template URL available')
        showLoadingDialog.value = false
        isGenerating.value = false
        return
      }

      const response =
        await WordDocumentTemplatesService.wordDocumentTemplatesCreateWordDocumentTemplateDocxWithPlaceholdersCreate(
          {
            uuid: template.value.uuid || '',
            requestBody: {
              word_document_template_uuid: template.value.uuid || ''
            }
          }
        )

      if (
        response &&
        typeof response === 'object' &&
        'success' in response &&
        response.success &&
        'task_id' in response &&
        response.task_id
      ) {
        // Save task to localStorage for persistence
        currentTaskId.value = response.task_id as string
        saveTaskToStorage(response.task_id as string, template.value.uuid || '')

        // Start monitoring the async task
        await monitorPlaceholderGeneration(response.task_id as string)
      } else {
        console.error('Invalid response format or operation failed:', response)
        genericErrorToast()
        showLoadingDialog.value = false
        isGenerating.value = false
      }
    } catch (error) {
      console.error('Error generating template with placeholders:', error)
      genericErrorToast()
      showLoadingDialog.value = false
      isGenerating.value = false
    }
  }

  const monitorPlaceholderGeneration = async (taskId: string) => {
    try {
      // Prevent duplicate monitoring
      if (isMonitoring.value) {
        return
      }

      isMonitoring.value = true
      taskErrorHandled.value = false

      // Close any existing WebSocket connection
      if (currentWebSocket.value) {
        currentWebSocket.value.close()
        currentWebSocket.value = null
      }

      // Connect to WebSocket for real-time updates using the same base URL as API calls
      const apiUrl = import.meta.env.VITE_API_URL
      // Remove /api/ from the URL since WebSocket endpoints are at the root level
      const baseUrl = apiUrl
        .replace('/api/', '/')
        .replace('http://', 'ws://')
        .replace('https://', 'wss://')
      const wsUrl = `${baseUrl}ws/placeholder-generation/${taskId}/`
      const ws = new WebSocket(wsUrl)
      currentWebSocket.value = ws

      ws.onopen = () => {
        console.log('WebSocket connected for placeholder generation monitoring')
      }

      ws.onmessage = async (event) => {
        const data = JSON.parse(event.data)

        if (data.type === 'task_update') {
          // Update progress in the loading dialog
          generationProgress.value = data.progress || 0
          generationStatus.value = data.status || 'Processing...'
        } else if (data.type === 'task_complete') {
          ws.close()
          currentWebSocket.value = null
          isMonitoring.value = false

          // Clear task from storage
          if (template.value?.uuid) {
            clearTaskFromStorage(template.value.uuid)
          }

          // Refresh the template data
          const updatedTemplate = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve(
            {
              uuid: template.value?.uuid || ''
            }
          )) as SuperUserWordDocumentTemplate

          template.value = updatedTemplate
          useAiTemplate.value = true

          // Show success toast first
          saveSuccessToast()

          // Update UI state
          isGenerating.value = false
          showLoadingDialog.value = false
          generationProgress.value = 100
          generationStatus.value = 'Completed!'

          editorReloadKey.value++
        } else if (data.type === 'task_error') {
          console.error('Placeholder generation failed:', data.error)
          taskErrorHandled.value = true
          ws.close()
          currentWebSocket.value = null
          isMonitoring.value = false

          // Clear task from storage
          if (template.value?.uuid) {
            clearTaskFromStorage(template.value.uuid)
          }

          // Show specific error message from the task instead of generic error
          const errorMessage = data.status || data.error || 'Task failed'
          toast({
            title: t('placeholderGenerationFailed'),
            description: errorMessage,
            variant: 'destructive',
            duration: 5000
          })

          isGenerating.value = false
          showLoadingDialog.value = false
          generationProgress.value = 0
          generationStatus.value = 'Failed'
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        ws.close()
        currentWebSocket.value = null
        isMonitoring.value = false
        // Show specific WebSocket error message
        toast({
          title: t('connectionError'),
          description: t('websocketConnectionFailed'),
          variant: 'destructive',
          duration: 5000
        })
        isGenerating.value = false
        showLoadingDialog.value = false
        generationProgress.value = 0
        generationStatus.value = 'WebSocket connection failed'
      }

      ws.onclose = (event) => {
        currentWebSocket.value = null
        isMonitoring.value = false
        isGenerating.value = false
        showLoadingDialog.value = false
        generationProgress.value = 0
      }
    } catch (error) {
      console.error('Error monitoring placeholder generation:', error)
      isMonitoring.value = false
      // Show specific error message
      toast({
        title: t('monitoringError'),
        description: t('failedToStartMonitoring'),
        variant: 'destructive',
        duration: 5000
      })
      isGenerating.value = false
      showLoadingDialog.value = false
      generationProgress.value = 0
      generationStatus.value = 'Failed to start monitoring'
    }
  }

  const validateTemplate = () => {
    router.push({
      name: 'admin-word-template-validate',
      params: { uuid: template.value?.uuid },
      query: { use_ai_template: activeTab.value === 'ai' ? 'true' : 'false' }
    })
  }

  const openEditor = () => {
    if (template.value?.uuid) {
      router.push(ROUTES.ADMIN.WORD_TEMPLATES.EDITOR.replace(':uuid', template.value.uuid))
    }
  }

  const showCopyAiConfirmation = () => {
    if (!template.value || !template.value.ai_docx_template) {
      toast({
        variant: 'destructive',
        description: 'No AI template available to copy'
      })
      return
    }
    showCopyAiModal.value = true
  }

  const copyAiTemplateToCurrent = async () => {
    showCopyAiModal.value = false
    try {
      if (!template.value || !template.value.ai_docx_template) {
        toast({
          variant: 'destructive',
          description: 'No AI template available to copy'
        })
        return
      }

      isPreparing.value = true

      await WordDocumentTemplatesService.wordDocumentTemplatesCopyAiToCurrentCreate({
        uuid: template.value.uuid
      })

      // Reload template to get updated docx_template and document
      const updatedTemplate = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: template.value.uuid
      })) as SuperUserWordDocumentTemplate

      template.value = updatedTemplate

      toast({
        description: t('templateCopied')
      })

      // Force editor to reload by incrementing reload key
      editorReloadKey.value++
    } catch (error) {
      console.error('Error copying AI template:', error)
      genericErrorToast()
    } finally {
      isPreparing.value = false
    }
  }

  const showCopyOriginalConfirmation = () => {
    if (!template.value || !template.value.original_docx_template) {
      toast({
        variant: 'destructive',
        description: 'No original template available to copy'
      })
      return
    }
    showCopyOriginalModal.value = true
  }

  const copyOriginalToCurrent = async () => {
    showCopyOriginalModal.value = false
    try {
      if (!template.value || !template.value.original_docx_template) {
        toast({
          variant: 'destructive',
          description: 'No original template available to copy'
        })
        return
      }

      isPreparing.value = true

      await WordDocumentTemplatesService.wordDocumentTemplatesResetToOriginalCreate({
        uuid: template.value.uuid
      })

      // Reload template to get updated docx_template and document
      const updatedTemplate = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: template.value.uuid
      })) as SuperUserWordDocumentTemplate

      template.value = updatedTemplate

      toast({
        description: t('templateCopied')
      })

      // Force editor to reload by incrementing reload key
      editorReloadKey.value++
    } catch (error) {
      console.error('Error copying original template:', error)
      genericErrorToast()
    } finally {
      isPreparing.value = false
    }
  }

  watch(activeTab, (newValue) => {
    router.replace({
      query: { ...route.query, use_ai_template: newValue === 'ai' ? 'true' : 'false' }
    })
  })

  // Sync tab from URL when query changes without remount (e.g. browser back/forward, shared link)
  watch(
    () => route.query.use_ai_template,
    (queryValue) => {
      const tab = queryValue === 'true' ? 'ai' : 'living'
      if (activeTab.value !== tab) activeTab.value = tab
    }
  )

  // Watch for template changes to check for existing tasks
  // Note: checkForExistingTask is already called in onMounted, so we don't need to call it here
  // to avoid duplicate WebSocket connections
  watch(
    () => template.value?.uuid,
    async (newUuid) => {
      if (newUuid) {
        // Only check for existing tasks if we're not already monitoring one
        if (!currentTaskId.value) {
          await checkForExistingTask()
        }
      }
    }
  )
  // Handle ESC key to close side-by-side modal
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && showSideBySideModal.value) {
      showSideBySideModal.value = false
    }
  }

  // Watch for side-by-side modal opening/closing
  watch(
    () => showSideBySideModal.value,
    (isOpen, wasOpen) => {
      if (isOpen && !wasOpen) {
        // When modal opens, store the current original document URL
        // This prevents the viewer from reloading when template is refreshed
        if (template.value?.original_docx_template) {
          originalDocumentUrlForComparison.value = template.value.original_docx_template
        }
      } else if (wasOpen && !isOpen) {
        // When modal closes (was open, now closed), reload the editor
        // Small delay to ensure modal is fully closed before reloading
        nextTick(() => {
          editorReloadKey.value++
        })
      }
    }
  )

  // Function to refresh template data from backend
  const refreshTemplate = async () => {
    if (!template.value?.uuid) return
    try {
      const updatedTemplate = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: template.value.uuid
      })) as SuperUserWordDocumentTemplate
      template.value = updatedTemplate
    } catch (error) {
      console.error('Error refreshing template:', error)
    }
  }

  // Handler for OnlyOffice save events
  const handleOnlyOfficeSave = async (event: CustomEvent) => {
    // Only refresh if this is for our template's document
    if (event.detail?.documentUuid === template.value?.document?.uuid) {
      // Add a small delay to ensure backend has finished saving
      setTimeout(() => {
        refreshTemplate()
      }, 1000)
    }
  }

  onMounted(async () => {
    try {
      template.value = (await WordDocumentTemplatesService.wordDocumentTemplatesRetrieve({
        uuid: router.currentRoute.value.params.uuid.toString()
      })) as SuperUserWordDocumentTemplate

      // Load available placeholders
      placeholders.value = await PlaceholdersService.placeholdersList()

      // Check for existing task after template is loaded
      if (template.value?.uuid) {
        await checkForExistingTask()
      }
    } catch (error) {
      genericErrorToast()
    }

    // Add keyboard listener
    window.addEventListener('keydown', handleKeyDown)

    // Listen for OnlyOffice save events
    const onlyOfficeSaveHandler = (event: Event) => {
      handleOnlyOfficeSave(event as CustomEvent)
    }
    window.addEventListener('onlyoffice-document-saved', onlyOfficeSaveHandler)

    // Store handler reference for cleanup
    ;(window as any).__onlyOfficeSaveHandler = onlyOfficeSaveHandler
  })

  onUnmounted(() => {
    // Clean up WebSocket connection when component is destroyed
    if (currentWebSocket.value) {
      currentWebSocket.value.close()
      currentWebSocket.value = null
    }
    isMonitoring.value = false
    currentTaskId.value = null

    // Remove OnlyOffice save event listener
    const handler = (window as any).__onlyOfficeSaveHandler
    if (handler) {
      window.removeEventListener('onlyoffice-document-saved', handler)
      delete (window as any).__onlyOfficeSaveHandler
    }
    taskErrorHandled.value = false

    // Remove keyboard listener
    window.removeEventListener('keydown', handleKeyDown)
  })
</script>

<template>
  <div
    v-if="template"
    v-bind="$attrs"
    class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5"
  >
    <!-- Page Header -->
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: t('wordTemplates'), to: ROUTES.ADMIN.WORD_TEMPLATES.REVIEW },
          {
            title: template.name,
            to: ROUTES.ADMIN.WORD_TEMPLATES.REVIEW_DETAIL.replace(':uuid', template.uuid || '')
          }
        ]"
      />

      <div class="flex items-center justify-end gap-x-3">
        <DropdownMenu v-if="template.docx_template">
          <DropdownMenuTrigger as-child>
            <Button
              variant="ghost"
              class="flex h-9 w-9 p-0 data-[state=open]:bg-muted"
            >
              <EllipsisVerticalIcon class="h-5 w-5" />
              <span class="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            class="w-[200px]"
          >
            <DropdownMenuItem
              v-if="template?.docx_template"
              class="cursor-pointer"
              @click="downloadDOCX(false)"
            >
              <DownloadIcon class="mr-2 h-4 w-4" />
              {{ t('downloadOriginal') }}
            </DropdownMenuItem>
            <DropdownMenuItem
              v-if="template?.ai_docx_template"
              class="cursor-pointer"
              @click="downloadDOCX(true)"
            >
              <DownloadIcon class="mr-2 h-4 w-4" />
              {{ t('downloadGenerated') }}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          v-if="template.original_docx_template && template.document?.uuid"
          variant="secondary"
          @click="showSideBySideModal = true"
        >
          <Columns2Icon class="mr-2 h-4 w-4" />
          {{ t('sideBySide') }}
        </Button>
        <Button
          variant="secondary"
          :disabled="isGenerating"
          @click="generatePlaceholders"
        >
          <Loader2
            v-if="isGenerating"
            class="mr-2 h-4 w-4 animate-spin"
          />
          <FileTextIcon
            v-else
            class="mr-2 h-4 w-4"
          />
          {{ t('generatePlaceholders') }}
        </Button>
        <Button
          variant="secondary"
          :disabled="!template?.replaceable_variables"
          @click="validateTemplate"
        >
          <FileTextIcon class="mr-2 h-4 w-4" />
          {{ t('validateTemplate') }}
        </Button>
        <Button
          v-if="template.status !== WordTemplateStatus.APPROVED"
          variant="default"
          :disabled="isSaving"
          @click="openAcceptModal"
        >
          <div class="flex items-center">
            <span>{{ t('saveChanges') }}</span>
          </div>
        </Button>
        <Button
          v-else
          variant="secondary"
          disabled
        >
          <div class="flex items-center">
            <span>{{ t('alreadyApproved') }}</span>
          </div>
        </Button>
      </div>
    </div>

    <div class="flex flex-row gap-5">
      <!-- Template Details Section - 20% width -->
      <div
        class="template-details-section rounded-xl border border-gray-200 bg-white p-5 shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800"
      >
        <div class="mb-3 flex flex-col gap-2">
          <h2 class="text-base font-semibold">{{ t('templateDetails') }}</h2>
          <!-- Status badge -->
          <div
            v-if="template.status === WordTemplateStatus.APPROVED"
            class="w-fit rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800"
          >
            Approved
          </div>
          <div
            v-else-if="template.status === WordTemplateStatus.IN_REVIEW"
            class="w-fit rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800"
          >
            {{ t('underReview') }}
          </div>
        </div>

        <!-- Template Details -->
        <div class="w-full">
          <div class="mb-4">
            <InputField
              v-model="template.name"
              name="name"
              label="Name"
              :rules="yup.string().required()"
              :disabled="true"
            />
            <InputField
              v-model="template.law_field"
              name="law_field"
              label="Rechtsgebiet"
              :disabled="true"
            />
            <InputField
              v-model="template.law_topic"
              name="law_topic"
              title="Rechtsthema"
              label="Rechtsthema"
              :disabled="true"
            />
            <TextAreaField
              v-model="template.description"
              name="description"
              label="Beschreibung"
              :disabled="true"
            />
          </div>

          <!-- Display original template if it exists -->
          <div
            v-if="template.original_docx_template"
            class="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3"
          >
            <div class="flex flex-col gap-2">
              <div>
                <h3 class="text-xs font-medium text-gray-700">{{ t('originalTemplate') }}</h3>
                <p class="truncate text-xs text-gray-600">
                  {{ getCleanUrl(template.original_docx_template) }}
                </p>
              </div>
              <div class="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  class="w-full justify-start text-xs"
                  @click="showCopyOriginalConfirmation"
                >
                  <CopyIcon class="mr-2 h-3 w-3" />
                  {{ t('copyToCurrent') }}
                </Button>
                <a
                  :href="template.original_docx_template"
                  download
                  class="inline-flex w-full items-center justify-start gap-x-2 rounded-md bg-white px-2 py-1.5 text-xs
                    font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  <DownloadIcon class="h-3 w-3 text-gray-500" />
                  {{ t('download') }}
                </a>
              </div>
            </div>
          </div>

          <!-- Display AI generated template if it exists -->
          <div
            v-if="template.ai_docx_template"
            class="mb-4 rounded-lg border border-purple-200 bg-purple-50 p-3"
          >
            <div class="flex flex-col gap-2">
              <div>
                <h3 class="text-xs font-medium text-purple-700">{{ t('aiGeneratedTemplate') }}</h3>
                <p class="truncate text-xs text-purple-700">
                  {{ getCleanUrl(template.ai_docx_template) }}
                </p>
              </div>
              <div class="flex flex-col gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  class="w-full justify-start text-xs"
                  @click="showCopyAiConfirmation"
                >
                  <CopyIcon class="mr-2 h-3 w-3" />
                  {{ t('copyAiToCurrent') }}
                </Button>
                <a
                  :href="template.ai_docx_template"
                  download
                  class="inline-flex w-full items-center justify-start gap-x-2 rounded-md bg-white px-2 py-1.5 text-xs
                    font-semibold text-purple-900 shadow-sm ring-1 ring-inset ring-purple-300 hover:bg-purple-50"
                >
                  <DownloadIcon class="h-3 w-3 text-purple-500" />
                  {{ t('download') }}
                </a>
              </div>
            </div>
          </div>

          <!-- Display current document if it exists -->
          <div
            v-if="template.docx_template"
            class="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3"
          >
            <div class="flex flex-col gap-2">
              <div>
                <h3 class="text-xs font-medium text-blue-700">{{ t('currentTemplate') }}</h3>
                <p class="truncate text-xs text-blue-700">
                  {{ docxTemplateCleanUrl }}
                </p>
              </div>
              <div class="flex flex-col gap-2">
                <a
                  :href="template.docx_template"
                  download
                  class="inline-flex w-full items-center justify-start gap-x-2 rounded-md bg-white px-2 py-1.5 text-xs
                    font-semibold text-blue-900 shadow-sm ring-1 ring-inset ring-blue-300 hover:bg-blue-50"
                >
                  <DownloadIcon class="h-3 w-3 text-blue-500" />
                  {{ t('download') }}
                </a>
              </div>
            </div>
          </div>

          <!-- File upload field -->
          <div class="mb-4">
            <label class="mb-2 block text-sm font-medium text-gray-800">{{ t('document') }}</label>
            <FileField
              :key="fileFieldKey"
              v-model="uploadedFile"
              name="docx_template"
              :label="template.docx_template ? t('replaceDocument') : t('uploadDocument')"
              :allowed-file-extensions="['docx']"
              @update:model-value="handleFileUpload"
            />
          </div>
        </div>
      </div>

      <!-- Document Preview Section - 80% width -->
      <div
        v-if="template && template.docx_template"
        class="document-preview-section rounded-xl border border-gray-200 bg-white shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800"
      >
        <!-- Office Editor Embedded -->
        <div
          v-if="template?.document?.uuid && !showSideBySideModal"
          class="office-editor-container"
        >
          <OfficeEditor
            :key="`${editorKey}-${editorReloadKey}`"
            :document-uuid="template.document.uuid"
            :word-template-uuid="template.uuid"
            :check-for-navbar="false"
          />
        </div>

        <!-- Fallback message if no document exists -->
        <div
          v-else
          class="flex flex-col items-center justify-center space-y-4 rounded-lg border-2 border-dashed
            border-gray-300 bg-gray-50 p-12"
        >
          <FileTextIcon class="h-16 w-16 text-gray-400" />
          <p class="text-lg font-medium text-gray-900">{{ t('editTemplate') }}</p>
          <p class="text-sm text-gray-500">No document available for editing</p>
        </div>
      </div>
    </div>

    <!-- Document Preview is now placed next to the template details in a 20-80 layout -->
    <!-- See the updated layout above -->
  </div>

  <!-- Accept Template Confirmation Modal -->
  <Dialog
    :open="showAcceptModal"
    @update:open="showAcceptModal = $event"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t('acceptTemplate') }}</DialogTitle>
        <DialogDescription>
          {{ t('acceptTemplateConfirmation') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-row justify-end gap-2 sm:justify-end">
        <Button
          variant="secondary"
          @click="showAcceptModal = false"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="default"
          :disabled="isSaving"
          @click="handleSave"
        >
          <div class="flex items-center">
            <span>{{ t('accept') }}</span>
            <span
              v-if="isSaving"
              class="ml-2 h-4 w-4 animate-spin rounded-full border-b-2 border-white"
            ></span>
          </div>
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Copy AI Template Confirmation Modal -->
  <Dialog
    :open="showCopyAiModal"
    @update:open="showCopyAiModal = $event"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t('copyAiConfirmTitle') }}</DialogTitle>
        <DialogDescription>
          {{ t('copyAiConfirmMessage') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-row justify-end gap-2 sm:justify-end">
        <Button
          variant="secondary"
          @click="showCopyAiModal = false"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="default"
          @click="copyAiTemplateToCurrent"
        >
          {{ t('confirm') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Copy Original Template Confirmation Modal -->
  <Dialog
    :open="showCopyOriginalModal"
    @update:open="showCopyOriginalModal = $event"
  >
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{ t('copyOriginalConfirmTitle') }}</DialogTitle>
        <DialogDescription>
          {{ t('copyOriginalConfirmMessage') }}
        </DialogDescription>
      </DialogHeader>
      <DialogFooter class="flex flex-row justify-end gap-2 sm:justify-end">
        <Button
          variant="secondary"
          @click="showCopyOriginalModal = false"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          variant="default"
          @click="copyOriginalToCurrent"
        >
          {{ t('confirm') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>

  <!-- Loading Dialog -->
  <Dialog
    :open="showLoadingDialog"
    :modal="true"
  >
    <DialogContent class="sm:max-w-md">
      <div class="flex flex-col items-center justify-center space-y-4 p-6">
        <div
          class="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent"
        ></div>
        <p class="text-lg font-medium">{{ $t('Generating document with placeholders...') }}</p>

        <!-- Progress Bar -->
        <div class="w-full space-y-2">
          <div class="flex justify-between text-sm text-muted-foreground">
            <span>{{ generationStatus }}</span>
            <span>{{ generationProgress }}%</span>
          </div>
          <div class="h-2 w-full rounded-full bg-gray-200">
            <div
              class="h-2 rounded-full bg-primary transition-all duration-300 ease-in-out"
              :style="{ width: `${generationProgress}%` }"
            ></div>
          </div>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <!-- Side by Side Comparison Modal -->
  <Teleport to="body">
    <div
      v-if="showSideBySideModal"
      class="side-by-side-overlay"
      @click.self="showSideBySideModal = false"
    >
      <div class="side-by-side-modal">
        <div class="side-by-side-header">
          <h2 class="text-lg font-semibold">{{ t('compareView') }}</h2>
          <Button
            variant="ghost"
            size="sm"
            @click="showSideBySideModal = false"
          >
            {{ t('closeComparison') }}
          </Button>
        </div>
        <div class="side-by-side-content">
          <!-- Left: Original Template (Read-only using Microsoft Office Online Viewer) -->
          <div class="side-by-side-pane">
            <div class="pane-header">
              <h3 class="text-sm font-semibold">{{ t('originalVersion') }}</h3>
            </div>
            <div class="pane-body">
              <MicrosoftOfficeViewer
                v-if="originalDocumentUrlForComparison"
                :key="originalDocumentUrlForComparison"
                :document-url="originalDocumentUrlForComparison"
                height="100%"
              />
            </div>
          </div>

          <!-- Right: Current Template (Editable) -->
          <div class="side-by-side-pane">
            <div class="pane-header">
              <h3 class="text-sm font-semibold">{{ t('currentVersion') }}</h3>
            </div>
            <div class="pane-body">
              <OfficeEditor
                v-if="template?.document?.uuid"
                :document-uuid="template.document.uuid"
                :word-template-uuid="template.uuid"
                :check-for-navbar="false"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
  /* Custom styling for highlighted text in PDF */
  :deep(.highlight) {
    background-color: rgba(69, 154, 252, 0.3); /* Blue highlight with transparency */
    border: 2px dashed rgba(69, 154, 252, 0.3); /* Light blue dashed border */
    border-radius: 2px;
    padding: 0;
    margin: -2 -1;
    box-shadow: 0 0 4px rgba(69, 154, 252, 0.5);
  }

  .pdf-container {
    max-width: 100%;
    overflow: auto;
  }

  /* Side by Side Modal Styles */
  .side-by-side-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .side-by-side-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: white;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .side-by-side-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
    flex-shrink: 0;
  }

  .side-by-side-content {
    display: flex;
    flex-direction: row;
    flex: 1;
    width: 100%;
    overflow: hidden;
  }

  .side-by-side-pane {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-right: 1px solid #e5e7eb;
  }

  .side-by-side-pane:last-child {
    border-right: none;
  }

  .pane-header {
    padding: 0.75rem 1rem;
    background: #f3f4f6;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .pane-body {
    flex: 1;
    overflow: hidden;
    background: white;
  }

  .template-details-section {
    width: 20%;
    min-width: 250px;
  }

  .document-preview-section {
    overflow: hidden;
    flex: 1;
  }

  .office-editor-container {
    height: 800px;
    width: 100%;
  }
</style>
